import Router from 'next/router';
import querystring from 'querystring';
import type { Dispatch } from 'redux';
import { type ActionsObject, makeAction, makeReducer } from '@finn-no/redux-actions';

import defaultState from './defaultState';
import { fetchOffers } from '../api/fetcher';
import { filtersToApiQueryParams, filtersToBrowserQueryParams } from '../lib/utils/queryParamFilterConversions';
import { updateBannerFilterValues } from 'shared/banners/bannerParams';
import { debounce } from 'shared/util/debounce';

import { ApiActionStatus, type FetchrData, type Results, type ThunkResult } from '../interfaces';

export const actions: ActionsObject<Results> = {};

actions.setPending = makeAction('RESULT_PENDING', (state) => ({
    ...state,
    status: ApiActionStatus.PENDING,
}));

actions.setResults = makeAction('RESULT_OK', (_, action) => ({
    ...defaultState.results,
    ...action.payload,
    status: ApiActionStatus.OK,
}));

actions.noChange = makeAction('RESULT_NO_CHANGE', (state) => ({
    ...state,
    status: ApiActionStatus.OK,
}));

actions.setError = makeAction('RESULT_ERROR', (state) => ({
    ...state,
    status: ApiActionStatus.ERROR,
}));

export const debouncedFetchOffers = debounce(async (fetch: () => Promise<FetchrData>, dispatch: Dispatch) => {
    const { statusCode, data } = await fetch();

    switch (statusCode) {
        case 200:
            dispatch(actions.setResults(data));
            break;
        case 304:
            dispatch(actions.noChange());
            break;
        default:
            dispatch(actions.setError());
    }
}, 300);

export const fetchResults =
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        (payload?: any): ThunkResult<Promise<void>> =>
        async (dispatch, getState): Promise<void> => {
            dispatch(actions.setPending());
            const queryParams = querystring.stringify(filtersToBrowserQueryParams(getState().filters));
            const destinations = getState().filters?.destinations?.list || [];
            const cities = destinations.map((destination) => destination.split(';')[2]).filter((city) => !!city);
            const countries = destinations.map((destination) => destination.split(';')[1]).filter((country) => !!country);
            updateBannerFilterValues({
                ...getState().filters,
                destination: {
                    city: cities[0],
                    country: countries[0],
                },
                page: Number.parseInt(getState().filters.page),
            });
            await Router.replace({ pathname: Router.pathname, query: queryParams }, undefined, { shallow: true });
            debouncedFetchOffers(() => fetchOffers(filtersToApiQueryParams({ ...getState().filters, ...payload })), dispatch);
        };

export const resultReducer = makeReducer<Results>(actions);
